/**
 * Aujourd'hui — l'écran d'atterrissage
 *
 * Il doit répondre à une question en dix secondes : qu'est-ce que je dois faire
 * aujourd'hui, et qu'est-ce qui arrive bientôt ?
 *
 * Deux partis pris du dossier de design, appliqués tels quels :
 *
 *  - **une phrase de synthèse humaine**, pas un compteur froid. « 2 choses à
 *    faire aujourd'hui, rien en retard » se lit ; « 2 / 14 » se déchiffre ;
 *  - **l'état vide est une récompense.** La journée où il n'y a rien à faire doit
 *    être satisfaisante, pas décevante.
 */

import { useMemo, useState } from 'react';
import { Check, Plus, Undo2 } from 'lucide-react';

import {
  type Tache,
  type Utilisateur,
  aujourdhuiParis,
  enClair,
  joursEntre,
  meConcerne,
} from '@foyer/noyau';

import { CarteTache, Chargement, Erreur, Section, TRAIT, Vide } from '../composants/ui.js';
import { MaisonAuSoleil } from '../composants/illustrations.js';
import { MiseEnFile } from '../lib/api.js';
import { useAppli, useRessource } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';
import { FeuilleTache } from './FeuilleTache.js';

/**
 * Phrase de synthèse : humaine, et jamais culpabilisante.
 *
 * Elle décrit **ce que l'écran montre**. Les tâches reportées en sont donc
 * exclues, comme elles le sont des sections : annoncer « 2 en retard » au-dessus
 * d'une liste qui en affiche une seule fait douter de l'application entière.
 */
export function synthese(taches: Tache[], aujourdhui: string): string {
  const avecDate = taches.filter(
    (t) => t.echeance && (!t.reporteA || t.reporteA <= aujourdhui),
  );
  const retard = avecDate.filter((t) => joursEntre(t.echeance!, aujourdhui) > 0).length;
  const dujour = avecDate.filter((t) => t.echeance === aujourdhui).length;

  if (retard === 0 && dujour === 0) return 'Rien à faire aujourd’hui, rien en retard.';
  if (retard === 0) {
    return dujour === 1 ? '1 chose à faire aujourd’hui, rien en retard.' : `${dujour} choses à faire aujourd’hui, rien en retard.`;
  }
  const partRetard = retard === 1 ? '1 en retard' : `${retard} en retard`;
  if (dujour === 0) return `Rien pour aujourd’hui, ${partRetard}.`;
  return `${dujour === 1 ? '1 chose' : `${dujour} choses`} à faire aujourd’hui, ${partRetard}.`;
}

export function Aujourdhui() {
  const { rafraichis, utilisateur, majUtilisateur } = useAppli();
  const aujourdhui = aujourdhuiParis();

  const taches = useRessource(() => donnees.taches({ statut: 'active' }));
  const foyer = useRessource(() => donnees.foyer());
  const vacances = useRessource(() => donnees.vacances());

  const [feuille, setFeuille] = useState<{ tache?: Tache; titre?: string } | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [dernierCochage, setDernierCochage] = useState<{ id: string; titre: string } | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const membres = foyer.donnee?.membres ?? [];
  const prenomDe = (id: string | null) =>
    id && id !== utilisateur?.id ? (membres.find((m: Utilisateur) => m.id === id)?.prenom ?? null) : null;

  /*
   * « Pour moi » masque ce qui revient à quelqu'un d'autre. Le filtre est un
   * réglage de personne, gardé côté serveur : le même choix suit le téléphone
   * et le navigateur, et c'est lui qui décide aussi des annonces — être
   * prévenu d'une tâche qu'on a choisi de ne pas voir n'aurait aucun sens.
   */
  const masque = utilisateur?.masqueAutres ?? false;

  async function changeFiltre(valeur: boolean) {
    if (!utilisateur) return;
    // Bascule optimiste : l'écran répond au doigt, le serveur suit.
    majUtilisateur({ ...utilisateur, masqueAutres: valeur });
    try {
      const r = await donnees.majNotifications({ masqueAutres: valeur });
      majUtilisateur(r.utilisateur);
    } catch (err) {
      // Hors ligne, la file rejouera l'écriture : on garde la bascule locale.
      if (!(err instanceof MiseEnFile)) {
        majUtilisateur({ ...utilisateur, masqueAutres: !valeur });
        setMessage((err as Error).message);
      }
    }
  }

  const groupes = useMemo(() => {
    /**
     * Les tâches reportées sont écartées des groupes, mais pas perdues : elles
     * sont comptées et consultables en bas d'écran. Les masquer sans rien dire
     * ferait croire à une disparition — et il n'y aurait plus de chemin pour
     * revenir en arrière.
     */
    const toutes = (taches.donnee?.taches ?? [])
      .filter((t) => t.statut === 'active')
      .filter((t) => !masque || meConcerne(t, utilisateur?.id ?? null));
    const reportees = toutes.filter((t) => t.reporteA && t.reporteA > aujourdhui);
    const liste = toutes.filter((t) => !t.reporteA || t.reporteA <= aujourdhui);
    const avecDate = liste.filter((t) => t.echeance).sort((a, b) => a.echeance!.localeCompare(b.echeance!));
    const jours = (t: Tache) => joursEntre(aujourdhui, t.echeance!);
    return {
      retard: avecDate.filter((t) => jours(t) < 0),
      jour: avecDate.filter((t) => jours(t) === 0),
      semaine: avecDate.filter((t) => jours(t) > 0 && jours(t) <= 7),
      mois: avecDate.filter((t) => jours(t) > 7 && jours(t) <= 31),
      plusTard: avecDate.filter((t) => jours(t) > 31),
      sansDate: liste.filter((t) => !t.echeance),
      reportees,
      // La phrase de synthèse décrit ce que l'écran montre : filtrée elle aussi,
      // sinon elle annoncerait des tâches qu'on ne voit pas.
      visibles: toutes,
    };
  }, [taches.donnee, aujourdhui, masque, utilisateur?.id]);


  /**
   * Assigner depuis la liste.
   *
   * Même traitement hors ligne que le cochage : la file d'attente garde le
   * geste et le rejouera. Dire « impossible » alors que c'est seulement
   * différé ferait recommencer pour rien.
   */
  async function assigne(tache: Tache, id: string | null) {
    setOccupe(tache.id);
    setMessage(null);
    try {
      await donnees.majTache(tache.id, { assigneA: id });
      rafraichis();
    } catch (err) {
      if (err instanceof MiseEnFile) {
        setMessage('Hors ligne : l’assignation partira au retour du réseau.');
        rafraichis();
      } else {
        setMessage((err as Error).message);
      }
    } finally {
      setOccupe(null);
    }
  }

  async function metEnSourdine(tache: Tache, muette: boolean) {
    setOccupe(tache.id);
    setMessage(null);
    try {
      await donnees.sourdine(tache.id, muette);
      rafraichis();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Réglage impossible');
    } finally {
      setOccupe(null);
    }
  }

  async function reporte(tache: Tache, jours: number) {
    setOccupe(tache.id);
    setMessage(null);
    try {
      await donnees.reporte(tache.id, jours);
      rafraichis();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Report impossible');
    } finally {
      setOccupe(null);
    }
  }

  async function coche(tache: Tache) {
    setOccupe(tache.id);
    setMessage(null);
    try {
      const r = await donnees.coche(tache.id, tache.titre, { majDernierEntretien: Boolean(tache.equipementId) });
      setDernierCochage({ id: tache.id, titre: tache.titre });
      if (r.avertissements?.length) setMessage(r.avertissements.join(' '));
      rafraichis();
    } catch (err) {
      if (err instanceof MiseEnFile) {
        setDernierCochage({ id: tache.id, titre: tache.titre });
        setMessage('Hors ligne : le cochage partira au retour du réseau.');
        rafraichis();
      } else {
        setMessage((err as Error).message);
      }
    } finally {
      setOccupe(null);
    }
  }

  async function annule() {
    if (!dernierCochage) return;
    setOccupe(dernierCochage.id);
    try {
      await donnees.annuleCochage(dernierCochage.id, dernierCochage.titre);
      setDernierCochage(null);
      setMessage(null);
      rafraichis();
    } catch (err) {
      if (err instanceof MiseEnFile) {
        setDernierCochage(null);
        rafraichis();
      } else setMessage((err as Error).message);
    } finally {
      setOccupe(null);
    }
  }

  if (taches.chargement && !taches.donnee) return <Chargement quoi="Chargement de la journée" />;
  if (taches.erreur && !taches.donnee) return <Erreur message={taches.erreur} />;

  const rien =
    groupes.retard.length === 0 &&
    groupes.jour.length === 0 &&
    groupes.semaine.length === 0 &&
    groupes.mois.length === 0 &&
    groupes.sansDate.length === 0;

  /*
   * Les rangées d'une tranche vivent dans un contenant commun : c'est lui qui
   * porte la boîte arrondie, et les rangées se séparent par un filet pointillé.
   * Une boîte par tâche empilait quinze contours pour rien.
   */
  const liste = (taches_: Tache[]) => (
    <div className="liste-taches">
      {taches_.map((t) => (
        <CarteTache
          key={t.id}
          tache={t}
          aujourdhui={aujourdhui}
          assigneAutre={prenomDe(t.assigneA)}
          membres={membres}
          moiId={utilisateur?.id ?? null}
          occupe={occupe === t.id}
          onCoche={() => void coche(t)}
          onOuvre={() => setFeuille({ tache: t })}
          onAssigne={(id) => void assigne(t, id)}
          onReporte={(jours) => void reporte(t, jours)}
          onAnnuleReport={() => void reporte(t, 0)}
          onSourdine={(muette) => void metEnSourdine(t, muette)}
        />
      ))}
    </div>
  );

  return (
    <>
      <div className="entete-vue">
        <h1>
          Aujourd’hui
        </h1>
      </div>
      <p className="chapeau">{synthese(groupes.visibles, aujourdhui)}</p>

      {membres.length > 1 && (
        <div className="puces" role="group" aria-label="Filtrer les tâches affichées">
          {(
            [
              [false, 'Tout le foyer'],
              [true, 'Pour moi'],
            ] as [boolean, string][]
          ).map(([valeur, libelle]) => (
            <button
              key={libelle}
              type="button"
              className={`puce${masque === valeur ? ' active' : ''}`}
              aria-pressed={masque === valeur}
              onClick={() => void changeFiltre(valeur)}
            >
              {libelle}
            </button>
          ))}
        </div>
      )}

      {vacances.donnee?.enVacances && (
        <div className="note">
          {vacances.donnee.enVacances.description} — jusqu’au {enClair(vacances.donnee.enVacances.fin)}.
        </div>
      )}

      {message && <div className="note">{message}</div>}

      {dernierCochage && (
        <div className="succes" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Check strokeWidth={TRAIT} size={18} aria-hidden />
          <span style={{ flex: 1 }}>« {dernierCochage.titre} » — c’est fait.</span>
          <button type="button" className="bouton texte" onClick={() => void annule()}>
            <Undo2 strokeWidth={TRAIT} size={16} aria-hidden />
            Annuler
          </button>
        </div>
      )}

      {rien ? (
        <Vide
          illustration={<MaisonAuSoleil />}
          titre="Tout est à jour."
          texte="Rien à faire aujourd’hui, rien en retard. Profitez-en."
        >
          <button type="button" className="bouton secondaire" onClick={() => setFeuille({})}>
            <Plus strokeWidth={TRAIT} size={18} aria-hidden />
            Ajouter une tâche
          </button>
        </Vide>
      ) : (
        <>
          <Section titre="En retard" compte={groupes.retard.length} urgente>
            {liste(groupes.retard)}
          </Section>
          <Section titre="Aujourd’hui" compte={groupes.jour.length}>
            {liste(groupes.jour)}
          </Section>
          <Section titre="Cette semaine" compte={groupes.semaine.length}>
            {liste(groupes.semaine)}
          </Section>
          <Section titre="Ce mois-ci" compte={groupes.mois.length} ouverteParDefaut={false}>
            {liste(groupes.mois)}
          </Section>
          <Section titre="Sans date" compte={groupes.sansDate.length} ouverteParDefaut={false}>
            {liste(groupes.sansDate)}
          </Section>
          <Section titre="Plus tard" compte={groupes.plusTard.length} ouverteParDefaut={false}>
            {liste(groupes.plusTard)}
          </Section>
        </>
      )}

      {/* Hors du ternaire, et c'est le point important : si TOUT est reporté,
          l'écran affiche « tout est à jour » et cette section reste le seul
          chemin de retour. Placée dans la branche « il reste des choses », elle
          disparaissait précisément quand elle devenait indispensable.
          `Section` ne rend rien quand le compte est nul : aucun vide inutile. */}
      <Section titre="Reportées" compte={groupes.reportees.length} ouverteParDefaut={false}>
        {liste(groupes.reportees)}
      </Section>

      {/*
        Ajout rapide : un bouton flottant plutôt qu'une barre pleine largeur.
        La barre coûtait 84 px d'écran en permanence — une tâche et demie — pour
        un champ utilisé quelques secondes par jour. Le bouton ouvre la feuille,
        qui a un vrai champ.
      */}
      <button
        type="button"
        className="ajout-rapide"
        aria-label="Ajouter une tâche"
        onClick={() => setFeuille({ titre: '' })}
      >
        <Plus strokeWidth={TRAIT} size={24} aria-hidden />
      </button>

      {feuille && (
        <FeuilleTache
          tache={feuille.tache}
          titreInitial={feuille.titre}
          membres={membres}
          periodes={vacances.donnee?.periodes ?? []}
          onFerme={() => setFeuille(null)}
        />
      )}
    </>
  );
}
